import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import toast from "react-hot-toast";
import { CSVLink } from "react-csv";

import { axiosConfig } from "../../axios/axiosConfig";
import PaginationControls from "../../components/Pagination/PaginationControls";

import TimelineIcon from "@mui/icons-material/Timeline";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import DownloadIcon from "@mui/icons-material/Download";

// ── palette (brand) ──────────────────────────────────────────────────────
const pageBg = "#eef2f7";
const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const navy = "#04343a";
const brandGreen = "#016551";
const textMain = "#1e293b";
const textMuted = "#64748b";

const LS_KEY = "customer_journey_calling_leads_v1";

// ── event master config (mirrors the backend customer_event_master table) ─
const EVENT_MASTER = [
  { key: "home_screen", label: "Home Screen", match: ["home_screen", "home"], score: 0, stage: "App/User Active", color: "#94a3b8" },
  { key: "screener_screen", label: "Screener", match: ["screener_screen", "screener"], score: 20, stage: "Interested", color: "#0284c7" },
  { key: "premium_calls", label: "Premium Calls", match: ["premiumcalls", "premium_calls"], score: 40, stage: "Product Interested", color: "#7c3aed" },
  { key: "futures_options", label: "Futures & Options", match: ["futuresoptions", "futures_options", "futures_&_options"], score: 60, stage: "High Intent", color: "#b45309" },
  { key: "niftyfifty_screen", label: "Nifty 50", match: ["niftyfifty_screen", "niftyfifty", "nifty50", "nifty_50"], score: 80, stage: "Very High Intent", color: "#ea580c" },
  { key: "upgrade_plan", label: "Upgrade Plan", match: ["upgradeplan", "upgrade_plan"], score: 100, stage: "Qualified Lead", color: brandGreen },
];

const normalizeName = (s) => (s || "").toString().trim().toLowerCase().replace(/[\s-]+/g, "_");

const findEventMeta = (eventName) => {
  const norm = normalizeName(eventName);
  return (
    EVENT_MASTER.find((e) => e.match.includes(norm)) ||
    EVENT_MASTER.find((e) => norm.includes(e.key) || e.key.includes(norm)) ||
    null
  );
};

// ── calling portal config ────────────────────────────────────────────────
const CALL_STATUS_CHOICES = [
  "New Lead",
  "Pending Call",
  "Call Attempted",
  "Connected",
  "Not Connected",
  "Callback Requested",
  "Interested",
  "Not Interested",
  "Converted",
  "Not Converted",
  "Invalid Number",
];

const CALL_STATUS_CONFIG = {
  "New Lead": { bg: "#f1f5f9", color: "#64748b" },
  "Pending Call": { bg: "#fef9c3", color: "#854d0e" },
  "Call Attempted": { bg: "#e0f2fe", color: "#0369a1" },
  Connected: { bg: "#dbeafe", color: "#1d4ed8" },
  "Not Connected": { bg: "#fee2e2", color: "#b91c1c" },
  "Callback Requested": { bg: "#ede9fe", color: "#6d28d9" },
  Interested: { bg: "#dcfce7", color: "#15803d" },
  "Not Interested": { bg: "#fee2e2", color: "#b91c1c" },
  Converted: { bg: "#dcfce7", color: "#016551" },
  "Not Converted": { bg: "#f1f5f9", color: "#7f1d1d" },
  "Invalid Number": { bg: "#f1f5f9", color: "#475569" },
};

const DEFAULT_LEAD_INFO = {
  status: "New Lead",
  assignedAgent: "",
  callAttempt: 0,
  lastCallTime: null,
  callOutcome: "",
  remarks: "",
  convertedAt: null,
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj?.[k] !== undefined && obj?.[k] !== null && obj?.[k] !== "") return obj[k];
  }
  return null;
};

// ── normalize raw API payload (per-user completed_events summary) into a
// per-customer journey list ──────────────────────────────────────────────
const buildJourney = (rawList) => {
  if (!Array.isArray(rawList) || rawList.length === 0) return [];

  return rawList.map((r) => {
    const completedEvents = pick(r, ["completed_events", "completedEvents"]) || [];
    const matchedMetas = completedEvents.map((e) => findEventMeta(e)).filter(Boolean);
    const stageMeta = matchedMetas.reduce((best, m) => (m.score > (best?.score ?? -1) ? m : best), null) || EVENT_MASTER[0];
    const totalEvents = Number(pick(r, ["total_events", "totalEvents"])) || EVENT_MASTER.length - 1;
    const completedCount = Number(pick(r, ["completed_event_count", "completedEventCount"])) || completedEvents.length;
    const allCompleted = !!pick(r, ["all_events_completed", "allEventsCompleted"]);
    const userId = pick(r, ["user_id", "userId", "id"]);

    return {
      key: String(userId || pick(r, ["phoneno", "mobile", "mobile_number"]) || Math.random()),
      userId,
      name: pick(r, ["username", "customer_name", "name", "full_name"]),
      mobile: pick(r, ["phoneno", "mobile", "mobile_number", "phone"]),
      email: pick(r, ["email"]),
      userType: pick(r, ["user_type", "userType"]),
      points: Number(pick(r, ["score"])) || 0,
      completedEvents,
      completedCount,
      totalEvents,
      allCompleted,
      highestStageLabel: stageMeta.label,
      score: stageMeta.score,
      stageMeta,
      qualified: stageMeta.key === "upgrade_plan" || allCompleted,
    };
  });
};

// ── small UI building blocks ─────────────────────────────────────────────
const StatCard = ({ icon, iconColor, value, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", p: "16px 20px", flex: 1, minWidth: 200 }}>
    <Box sx={{ width: 48, height: 48, borderRadius: "50%", background: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {React.cloneElement(icon, { sx: { color: "#fff", fontSize: 26 } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: textMain, lineHeight: 1.1, fontFamily: "monospace" }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: textMuted, mt: "2px" }}>{label}</Typography>
    </Box>
  </Box>
);

const StageChip = ({ stageMeta }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      fontSize: 11, fontWeight: 700, color: "#fff", background: stageMeta.color,
      borderRadius: "999px", padding: "3px 10px", whiteSpace: "nowrap",
    }}
  >
    {stageMeta.score}% · {stageMeta.stage}
  </Box>
);

const CallStatusChip = ({ status }) => {
  const cfg = CALL_STATUS_CONFIG[status] || { bg: "#f1f5f9", color: "#64748b" };
  return <Chip label={status || "New Lead"} size="small" sx={{ fontWeight: 600, fontSize: 11, bgcolor: cfg.bg, color: cfg.color }} />;
};

// ── manage-call dialog ────────────────────────────────────────────────────
const ManageCallDialog = ({ open, target, leadInfo, defaultAgent, onClose, onSave }) => {
  const [status, setStatus] = useState(DEFAULT_LEAD_INFO.status);
  const [assignedAgent, setAssignedAgent] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    if (leadInfo) {
      setStatus(leadInfo.status || DEFAULT_LEAD_INFO.status);
      setAssignedAgent(leadInfo.assignedAgent || defaultAgent || "");
      setCallOutcome(leadInfo.callOutcome || "");
      setRemarks(leadInfo.remarks || "");
    }
  }, [leadInfo, target, defaultAgent]);

  if (!target) return null;

  const handleSave = (logAttempt) => {
    onSave(target.key, {
      ...leadInfo,
      status,
      assignedAgent,
      callOutcome,
      remarks,
      callAttempt: logAttempt ? (leadInfo?.callAttempt || 0) + 1 : leadInfo?.callAttempt || 0,
      lastCallTime: logAttempt ? new Date().toISOString() : leadInfo?.lastCallTime || null,
      convertedAt: status === "Converted" ? leadInfo?.convertedAt || new Date().toISOString() : leadInfo?.convertedAt || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ background: navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{target.name || target.mobile || target.userId || "Lead"}</Typography>
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>User ID: {target.userId || "—"} · Mobile: {target.mobile || "—"}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Calling Status</InputLabel>
          <Select label="Calling Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {CALL_STATUS_CHOICES.map((s) => (
              <MenuItem key={s} value={s}>
                <CallStatusChip status={s} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" label="Assigned Agent" fullWidth value={assignedAgent} onChange={(e) => setAssignedAgent(e.target.value)} />
        <TextField size="small" label="Call Outcome" fullWidth value={callOutcome} onChange={(e) => setCallOutcome(e.target.value)} placeholder="e.g. Discussed premium plan, will decide by Friday" />
        <TextField size="small" label="Remarks" fullWidth multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        <Typography sx={{ fontSize: 11, color: textMuted }}>
          Call attempts so far: <b>{leadInfo?.callAttempt || 0}</b> · Last call: {fmtDate(leadInfo?.lastCallTime)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="outlined" onClick={() => handleSave(false)}>Save Details</Button>
        <Button variant="contained" sx={{ background: brandGreen, "&:hover": { background: "#014a3d" } }} onClick={() => handleSave(true)}>
          Save &amp; Log Call Attempt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── main page ─────────────────────────────────────────────────────────────
const CustomerJourneyDashboard = () => {
  const authUser = useSelector((store) => store.auth);
  const agentName = authUser?.user?.userDetails?.username || authUser?.user?.userDetails?.email || "";

  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("All");
  const [userTypeFilter, setUserTypeFilter] = useState("All");
  const [callStatusFilter, setCallStatusFilter] = useState("All");
  const [apiPage, setApiPage] = useState(1);
  const [page2, setPage2] = useState(1);
  const [callingLeads, setCallingLeads] = useState({});
  const [manageTarget, setManageTarget] = useState(null);

  const PAGE_SIZE = 25;
  const API_PAGE_SIZE = 50;

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) setCallingLeads(JSON.parse(raw));
    } catch {
      // ignore malformed local cache
    }
  }, []);

  const persistCallingLeads = (next) => {
    setCallingLeads(next);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(next));
    } catch {
      // storage unavailable — state still updates for this session
    }
  };

  const loadData = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/advertisement/customer-app-journey-event/",
        params: { page },
      });
      const list =
        (Array.isArray(res.data?.results?.users) && res.data.results.users) ||
        (Array.isArray(res.data?.users) && res.data.users) ||
        (Array.isArray(res.data?.response) && res.data.response) ||
        (Array.isArray(res.data?.results) && res.data.results) ||
        (Array.isArray(res.data?.data) && res.data.data) ||
        (Array.isArray(res.data) && res.data) ||
        [];
      setRawData(list);
      const total =
        res.data?.count ??
        res.data?.Count ??
        res.data?.results?.total_users ??
        res.data?.results?.Count ??
        res.data?.results?.count ??
        res.data?.total_users ??
        res.data?.total ??
        null;
      const nextUrl = res.data?.next || res.data?.results?.next || null;
      setHasNextPage(!!nextUrl);
      // Fall back to an estimate (at least one more page than what we've seen)
      // when the backend doesn't send back an explicit total — this keeps
      // pagination visible/usable even if the total-count field name changes.
      setTotalCount(total ?? (nextUrl ? page * API_PAGE_SIZE + 1 : list.length));
    } catch (error) {
      console.log("customer-app-journey-event error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load customer journey data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(apiPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPage]);

  const journey = useMemo(() => buildJourney(rawData), [rawData]);

  const getLeadInfo = (key) => callingLeads[key] || DEFAULT_LEAD_INFO;

  const summary = useMemo(() => {
    const total = journey.length;
    const qualifiedCount = journey.filter((c) => c.qualified).length;
    const avgScore = total ? journey.reduce((s, c) => s + c.score, 0) / total : 0;
    const converted = journey.filter((c) => c.qualified && getLeadInfo(c.key).status === "Converted").length;
    const conversionRate = qualifiedCount ? (converted / qualifiedCount) * 100 : 0;
    return { total, qualifiedCount, avgScore, converted, conversionRate };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey, callingLeads]);

  const callingMetrics = useMemo(() => {
    const qualifiedList = journey.filter((c) => c.qualified);
    const leads = qualifiedList.map((c) => getLeadInfo(c.key));
    const count = (pred) => leads.filter(pred).length;
    return {
      totalQualified: qualifiedList.length,
      totalAssigned: count((l) => !!l.assignedAgent),
      totalAttempted: count((l) => (l.callAttempt || 0) > 0),
      totalConnected: count((l) => l.status === "Connected"),
      totalCallback: count((l) => l.status === "Callback Requested"),
      totalInterested: count((l) => l.status === "Interested"),
      totalConverted: count((l) => l.status === "Converted"),
      totalNotInterested: count((l) => l.status === "Not Interested"),
      totalNotConverted: count((l) => l.status === "Not Converted"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey, callingLeads]);

  const matchesSearch = (c) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [c.name, c.userId, c.mobile, c.email].some((v) => (v || "").toString().toLowerCase().includes(q));
  };

  const userTypeOptions = useMemo(
    () => Array.from(new Set(journey.map((c) => c.userType).filter(Boolean))).sort(),
    [journey]
  );

  const filteredJourney = useMemo(() => {
    return journey
      .filter(matchesSearch)
      .filter((c) => stageFilter === "All" || c.stageMeta.label === stageFilter)
      .filter((c) => userTypeFilter === "All" || c.userType === userTypeFilter)
      .sort((a, b) => b.score - a.score || b.points - a.points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey, search, stageFilter, userTypeFilter]);

  const qualifiedList = useMemo(() => {
    return journey
      .filter((c) => c.qualified)
      .filter(matchesSearch)
      .filter((c) => callStatusFilter === "All" || getLeadInfo(c.key).status === callStatusFilter)
      .sort((a, b) => b.points - a.points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey, search, callStatusFilter, callingLeads]);

  const pagedQualified = qualifiedList.slice((page2 - 1) * PAGE_SIZE, page2 * PAGE_SIZE);

  const handleSaveLead = (key, updated) => {
    persistCallingLeads({ ...callingLeads, [key]: updated });
    setManageTarget(null);
    toast.success("Calling lead updated");
  };

  const allCustomersCsvHeaders = [
    { label: "Customer Name", key: "name" },
    { label: "User ID", key: "userId" },
    { label: "Mobile Number", key: "mobile" },
    { label: "User Type", key: "userType" },
    { label: "Completed Events", key: "completedEventsLabel" },
    { label: "Progress", key: "progressLabel" },
    { label: "Highest Stage", key: "highestStageLabel" },
    { label: "Points", key: "points" },
  ];

  const allCustomersCsvData = filteredJourney.map((c) => ({
    ...c,
    completedEventsLabel: c.completedEvents.join(", "),
    progressLabel: `${c.completedCount}/${c.totalEvents}`,
  }));

  const csvHeaders = [
    { label: "Customer Name", key: "name" },
    { label: "User ID", key: "userId" },
    { label: "Mobile Number", key: "mobile" },
    { label: "Email", key: "email" },
    { label: "User Type", key: "userType" },
    { label: "Highest Stage", key: "highestStageLabel" },
    { label: "Points", key: "points" },
    { label: "Completed Events", key: "completedEventsLabel" },
    { label: "Progress", key: "progressLabel" },
    { label: "Calling Status", key: "callingStatus" },
    { label: "Assigned Agent", key: "assignedAgent" },
    { label: "Call Attempts", key: "callAttempt" },
    { label: "Remarks", key: "remarks" },
  ];

  const csvData = qualifiedList.map((c) => {
    const lead = getLeadInfo(c.key);
    return {
      ...c,
      completedEventsLabel: c.completedEvents.join(", "),
      progressLabel: `${c.completedCount}/${c.totalEvents}`,
      callingStatus: lead.status,
      assignedAgent: lead.assignedAgent,
      callAttempt: lead.callAttempt,
      remarks: lead.remarks,
    };
  });

  const headCellSx = {
    textAlign: "left", fontSize: 11, color: "#fff", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.5px",
    background: navy, p: "12px 14px", whiteSpace: "nowrap",
  };
  const cellSx = { p: "10px 14px", fontSize: 12.5, color: textMain, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };

  return (
    <Box sx={{ p: 3, background: pageBg, minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ background: navy, borderRadius: "12px", boxShadow: "0 4px 20px rgba(4,52,58,0.3)", p: "20px 24px", mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TimelineIcon sx={{ color: "#1db954", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 20, lineHeight: 1.2 }}>Customer Journey &amp; Lead Scoring</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mt: "2px" }}>
              Home → Screener → Premium Calls → Futures &amp; Options → Nifty 50 → Upgrade Plan
            </Typography>
          </Box>
        </Box>
        <Box
          component="button"
          onClick={() => loadData(apiPage)}
          sx={{
            display: "flex", alignItems: "center", gap: 0.75,
            background: brandGreen, color: "#fff", border: "none", borderRadius: "8px",
            padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
          }}
        >
          <RefreshIcon sx={{ fontSize: 16 }} /> Refresh
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: brandGreen }} />
        </Box>
      ) : (
        <>
          {/* Summary cards */}
          <Box sx={{ display: "flex", gap: 2.5, mb: 3, flexWrap: "wrap" }}>
            <StatCard icon={<GroupsIcon />} iconColor={navy} value={totalCount.toLocaleString("en-IN")} label="Tracked Customers" />
            <StatCard icon={<EmojiEventsIcon />} iconColor={brandGreen} value={summary.qualifiedCount.toLocaleString("en-IN")} label="Qualified Leads (100%)" />
            <StatCard icon={<TrendingUpIcon />} iconColor="#0284c7" value={`${summary.avgScore.toFixed(1)}%`} label="Average Score" />
            <StatCard icon={<SupportAgentIcon />} iconColor="#b45309" value={`${summary.conversionRate.toFixed(1)}%`} label="Calling Conversion" />
          </Box>

          {/* Search + tabs */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search by name, user ID, mobile or email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage2(1);
              }}
              sx={{ background: cardBg, borderRadius: "8px", minWidth: 280 }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: textMuted }} /></InputAdornment>) }}
            />
            {tab === 0 ? (
              <>
                <FormControl size="small" sx={{ minWidth: 200, background: cardBg, borderRadius: "8px" }}>
                  <InputLabel>Highest Stage</InputLabel>
                  <Select label="Highest Stage" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                    <MenuItem value="All">All Stages</MenuItem>
                    {EVENT_MASTER.map((s) => (
                      <MenuItem key={s.key} value={s.label}>{s.label} ({s.score}%)</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, background: cardBg, borderRadius: "8px" }}>
                  <InputLabel>User Type</InputLabel>
                  <Select label="User Type" value={userTypeFilter} onChange={(e) => setUserTypeFilter(e.target.value)}>
                    <MenuItem value="All">All User Types</MenuItem>
                    {userTypeOptions.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <CSVLink
                  data={allCustomersCsvData}
                  headers={allCustomersCsvHeaders}
                  filename={`customer-journey-page-${apiPage}.csv`}
                  style={{ textDecoration: "none" }}
                >
                  <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75, background: cardBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: "8px", padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", "&:hover": { background: "#f1f5f9" } }}>
                    <DownloadIcon sx={{ fontSize: 16 }} /> Export CSV
                  </Box>
                </CSVLink>
              </>
            ) : (
              <>
                <FormControl size="small" sx={{ minWidth: 200, background: cardBg, borderRadius: "8px" }}>
                  <InputLabel>Calling Status</InputLabel>
                  <Select label="Calling Status" value={callStatusFilter} onChange={(e) => { setCallStatusFilter(e.target.value); setPage2(1); }}>
                    <MenuItem value="All">All Statuses</MenuItem>
                    {CALL_STATUS_CHOICES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <CSVLink data={csvData} headers={csvHeaders} filename="qualified-leads.csv" style={{ textDecoration: "none" }}>
                  <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75, background: cardBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: "8px", padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", "&:hover": { background: "#f1f5f9" } }}>
                    <DownloadIcon sx={{ fontSize: 16 }} /> Export CSV
                  </Box>
                </CSVLink>
              </>
            )}
          </Box>

          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px 12px 0 0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", borderBottom: "none" }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ minHeight: 44, px: 1, "& .MuiTabs-indicator": { backgroundColor: brandGreen, height: 3 } }}>
              <Tab label={`All Customers (${totalCount.toLocaleString("en-IN")})`} sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, minHeight: 44, "&.Mui-selected": { color: navy } }} />
              <Tab label={`Qualified Leads / Calling Portal (${summary.qualifiedCount})`} sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, minHeight: 44, "&.Mui-selected": { color: brandGreen } }} />
            </Tabs>
          </Box>

          {tab === 0 ? (
            <>
              <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "0 0 12px 12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                    <Box component="thead">
                      <Box component="tr">
                        {["Sr. No.", "Customer", "User ID", "Mobile", "User Type", "Completed Events", "Highest Stage", "Points"].map((h) => (
                          <Box component="th" key={h} sx={headCellSx}>{h}</Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {filteredJourney.length === 0 ? (
                        <Box component="tr">
                          <Box component="td" colSpan={8} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>No tracked customers found</Box>
                        </Box>
                      ) : (
                        filteredJourney.map((c, i) => (
                          <Box component="tr" key={c.key} sx={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                            <Box component="td" sx={{ ...cellSx, fontFamily: "monospace" }}>{(apiPage - 1) * API_PAGE_SIZE + i + 1}</Box>
                            <Box component="td" sx={cellSx}>{c.name || "—"}</Box>
                            <Box component="td" sx={{ ...cellSx, fontFamily: "monospace" }}>{c.userId || "—"}</Box>
                            <Box component="td" sx={{ ...cellSx, fontFamily: "monospace" }}>{c.mobile || "—"}</Box>
                            <Box component="td" sx={cellSx}>{c.userType || "—"}</Box>
                            <Box component="td" sx={{ ...cellSx, whiteSpace: "normal", maxWidth: 260 }}>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: textMain }}>{c.completedCount}/{c.totalEvents}</Typography>
                              <Typography sx={{ fontSize: 11, color: textMuted }}>{c.completedEvents.join(", ") || "—"}</Typography>
                            </Box>
                            <Box component="td" sx={cellSx}><StageChip stageMeta={c.stageMeta} /></Box>
                            <Box component="td" sx={{ ...cellSx, fontWeight: 700, fontFamily: "monospace" }}>{c.points}</Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              {(totalCount > API_PAGE_SIZE || hasNextPage || apiPage > 1) && (
                <PaginationControls totalCount={totalCount} page={apiPage} setPage={setApiPage} offset={API_PAGE_SIZE} />
              )}
            </>
          ) : (
            <>
              {/* Calling performance metrics */}
              <Box sx={{ display: "flex", gap: 1.5, mb: 2, mt: 2, flexWrap: "wrap" }}>
                {[
                  ["Qualified", callingMetrics.totalQualified],
                  ["Assigned", callingMetrics.totalAssigned],
                  ["Attempted", callingMetrics.totalAttempted],
                  ["Connected", callingMetrics.totalConnected],
                  ["Callback Req.", callingMetrics.totalCallback],
                  ["Interested", callingMetrics.totalInterested],
                  ["Converted", callingMetrics.totalConverted],
                  ["Not Interested", callingMetrics.totalNotInterested],
                  ["Not Converted", callingMetrics.totalNotConverted],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "10px", p: "10px 16px", minWidth: 110, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: textMain, fontFamily: "monospace" }}>{value}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: textMuted, mt: "2px" }}>{label}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
                    <Box component="thead">
                      <Box component="tr">
                        {["Sr. No.", "Customer", "User ID", "Mobile", "Email", "Points", "Calling Status", "Assigned Agent", "Attempts", "Last Call", "Remarks", ""].map((h) => (
                          <Box component="th" key={h} sx={headCellSx}>{h}</Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {pagedQualified.length === 0 ? (
                        <Box component="tr">
                          <Box component="td" colSpan={12} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>No qualified leads yet</Box>
                        </Box>
                      ) : (
                        pagedQualified.map((c, i) => {
                          const lead = getLeadInfo(c.key);
                          return (
                            <Box component="tr" key={c.key} sx={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                              <Box component="td" sx={{ ...cellSx, fontFamily: "monospace" }}>{(page2 - 1) * PAGE_SIZE + i + 1}</Box>
                              <Box component="td" sx={cellSx}>{c.name || "—"}</Box>
                              <Box component="td" sx={{ ...cellSx, fontFamily: "monospace" }}>{c.userId || "—"}</Box>
                              <Box component="td" sx={{ ...cellSx, fontFamily: "monospace" }}>{c.mobile || "—"}</Box>
                              <Box component="td" sx={cellSx}>{c.email || "—"}</Box>
                              <Box component="td" sx={{ ...cellSx, fontWeight: 700, fontFamily: "monospace" }}>{c.points}</Box>
                              <Box component="td" sx={cellSx}><CallStatusChip status={lead.status} /></Box>
                              <Box component="td" sx={cellSx}>{lead.assignedAgent || "—"}</Box>
                              <Box component="td" sx={{ ...cellSx, fontFamily: "monospace" }}>{lead.callAttempt || 0}</Box>
                              <Box component="td" sx={cellSx}>{fmtDate(lead.lastCallTime)}</Box>
                              <Box component="td" sx={{ ...cellSx, maxWidth: 220, whiteSpace: "normal" }}>{lead.remarks || "—"}</Box>
                              <Box component="td" sx={cellSx}>
                                <Tooltip title="Manage call / status">
                                  <IconButton size="small" onClick={() => setManageTarget(c)} sx={{ color: brandGreen }}>
                                    <SupportAgentIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              {qualifiedList.length > PAGE_SIZE && (
                <PaginationControls totalCount={qualifiedList.length} page={page2} setPage={setPage2} offset={PAGE_SIZE} />
              )}
              <Typography sx={{ fontSize: 11, color: textMuted, mt: 1.5 }}>
                Calling status, agent assignment and remarks are currently saved locally in this browser — connect the customer_calling_leads backend API here once available.
              </Typography>
            </>
          )}
        </>
      )}

      <ManageCallDialog
        open={!!manageTarget}
        target={manageTarget}
        leadInfo={manageTarget ? getLeadInfo(manageTarget.key) : null}
        defaultAgent={agentName}
        onClose={() => setManageTarget(null)}
        onSave={handleSaveLead}
      />
    </Box>
  );
};

export default CustomerJourneyDashboard;
